import { Box, Divider, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import { ActionLink, CurrencyFormatters } from "halifax";
import React, { useContext, useMemo } from "react";
import {
  AncillaryKindEnum,
  BookedFlightItineraryWithDepartureTime,
  FortWorthSeat,
  MyTripsFilter,
  PaymentLineItemEnum,
  PaymentLineItemTravelWallet,
  PaymentLineItemTravelWalletEnum,
  PaymentTypeEnum,
  getDepartureSlice,
  getReturnSlice,
} from "redmond";

import * as textConstants from "./constants";
import "./styles.scss";
import { ClientContext } from "../../../../../../../../App";
import { usePrintItemizedReceipt } from "../../usePrintItemizedReceipt";

interface IFlightItemizedReceiptModalContentProps {
  flight: BookedFlightItineraryWithDepartureTime;
  documentId?: string;
  isMobile?: boolean;
  tripsFilter: MyTripsFilter;
}

export const FlightItemizedReceiptModalContent = ({
  flight,
  documentId = textConstants.DEFAULT_DOC_ID,
  isMobile,
  tripsFilter,
}: IFlightItemizedReceiptModalContentProps) => {
  const { bookedItinerary } = flight;
  const { sellingPricing, paymentBreakdown, travelItinerary } = bookedItinerary;
  const { pricingByPassenger, totalPricing } = sellingPricing;
  const { logo } = useContext(ClientContext);

  const breakdownPayments =
    paymentBreakdown?.payments as PaymentLineItemTravelWallet[];
  const walletItemPayments = breakdownPayments.filter(
    (p) => p.PaymentLineItem === PaymentLineItemEnum.TravelWallet
  );

  const confirmationCode = `H-${travelItinerary?.locators?.agent.unscopedValue}`;

  const travelerNames = bookedItinerary.passengers.alone.map(
    (p) => `${p.person.givenName} ${p.person.surname}`
  );
  bookedItinerary.passengers.withLapInfants.forEach((p) => {
    travelerNames.push(`${p.adult.person.givenName} ${p.adult.person.surname}`);
    travelerNames.push(
      `${p.infant.person.givenName} ${p.infant.person.surname}`
    );
  });
  const primaryTravelerName = travelerNames[0] ?? "";

  const departureSlice = getDepartureSlice(bookedItinerary);
  const returnSlice = getReturnSlice(bookedItinerary);

  const departureDate = departureSlice?.segments[0]?.scheduledDeparture;
  const returnDate = returnSlice?.segments[0]?.scheduledDeparture;

  const { currencyCode } = totalPricing.total.fiat;

  const addCurrency = (num: number) =>
    `${CurrencyFormatters.get(currencyCode).format(Math.abs(num))}`;

  const seatPricingByPassenger: { [id: string]: number } = {};
  if (bookedItinerary.seats?.seats?.length) {
    bookedItinerary.seats.seats.forEach((s: FortWorthSeat) => {
      seatPricingByPassenger[s.personId] =
        (seatPricingByPassenger[s.personId] ?? 0) + s.price;
    });
  }

  const cfar = totalPricing.ancillaries.find(
    (a) => a.kind === AncillaryKindEnum.Cfar
  );
  const disruptionProtection = totalPricing.ancillaries.find(
    (a) =>
      a.kind === AncillaryKindEnum.Delay ||
      a.kind === AncillaryKindEnum.MissedConnection
  );
  const { chfar } = totalPricing;

  // `total` excludes seat-selection cost (seats are priced separately), so add
  // the seat totals already computed above for the per-passenger rows.
  const seatsTotal = Object.values(seatPricingByPassenger).reduce(
    (acc, price) => acc + price,
    0
  );
  const totalCost = totalPricing.total.fiat.value + seatsTotal;
  const effectiveTotal =
    totalCost -
    walletItemPayments.reduce((acc, p) => acc + p.amount.amount, 0);

  const paymentInfo = {
    numberDisplay: "",
    rewardsDisplay: "",
    creditApplied: breakdownPayments.some(
      (p) =>
        p.TravelWallet === PaymentLineItemTravelWalletEnum.TravelWalletCredit
    ),
    offerApplied: breakdownPayments.some(
      (p) =>
        p.TravelWallet === PaymentLineItemTravelWalletEnum.TravelWalletOffer
    ),
  };

  const { paymentAmountInfo } = flight.bookedItinerary;
  if (paymentAmountInfo?.PaymentAmountInfo === PaymentTypeEnum.FiatAmountInfo) {
    paymentInfo.numberDisplay = paymentAmountInfo.numberDisplay;
  } else if (
    paymentAmountInfo?.PaymentAmountInfo === PaymentTypeEnum.RewardsAmountInfo
  ) {
    paymentInfo.numberDisplay = paymentAmountInfo.accountDisplayName;
  } else if (
    paymentAmountInfo?.PaymentAmountInfo === PaymentTypeEnum.SplitAmountInfo
  ) {
    paymentInfo.numberDisplay = paymentAmountInfo.fiatInfo.numberDisplay;
    paymentInfo.rewardsDisplay =
      paymentAmountInfo.rewardsInfo.accountDisplayName;
  }

  const { printing, handlePrint } = usePrintItemizedReceipt({
    documentId,
    tripId: bookedItinerary.id,
    travelType: "flight",
    tripsFilter,
    printWindowTitle: `${primaryTravelerName} - ${confirmationCode} ${textConstants.TITLE}`,
  });

  const renderPrintButton = useMemo(() => {
    if (printing || isMobile) return null;
    return (
      <ActionLink
        className="print-btn"
        content={textConstants.PRINT}
        onClick={handlePrint}
      />
    );
  }, [isMobile, printing]);

  return (
    <Box className="itemized-receipt-modal-container" id={documentId}>
      <Box className="itemized-receipt-modal-content">
        <Box className="itemized-receipt-logo-container">
          {logo}
          {renderPrintButton}
        </Box>
        <Box className="itemized-receipt-title-container">
          <Typography variant="h2">{textConstants.TITLE}</Typography>
        </Box>
        <Box className="itemized-receipt-details">
          {travelerNames.length === 1 ? (
            <Box className="line-item">
              <Typography variant="body1">
                {textConstants.TRAVELER_NAME}:
              </Typography>
              <Typography variant="body1" className="bold">
                {primaryTravelerName}
              </Typography>
            </Box>
          ) : (
            <Box className="line-item">
              <Typography variant="body1">
                {textConstants.TRAVELERS}:
              </Typography>
              <Box>
                {travelerNames.map((name) => (
                  <Typography key={name} variant="body1" className="bold right-align">
                    {name}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
          <Box className="line-item">
            <Typography variant="body1">
              {textConstants.CONFIRMATION_NUMBER}:
            </Typography>
            <Typography variant="body1" className="bold">
              {confirmationCode}
            </Typography>
          </Box>
          {departureDate && (
            <Box className="line-item">
              <Typography variant="body1">
                {textConstants.DEPARTURE}:
              </Typography>
              <Typography variant="body1" className="bold">
                {dayjs(departureDate).format(textConstants.MMDDYYYY_FORMAT)}
              </Typography>
            </Box>
          )}
          {returnDate && (
            <Box className="line-item gutter-bottom">
              <Typography variant="body1">{textConstants.RETURN}:</Typography>
              <Typography variant="body1" className="bold">
                {dayjs(returnDate).format(textConstants.MMDDYYYY_FORMAT)}
              </Typography>
            </Box>
          )}
          <Divider className="line-item gutter-bottom" />
          {pricingByPassenger.map(
            ({ person, baseWithoutMargin, taxes, passengerFee, additionalMargin }) => {
              const passengerName = `${person.givenName} ${person.surname}`;
              const taxesTotal =
                taxes.fiat.value +
                passengerFee.fiat.value +
                (additionalMargin?.fiat.value ?? 0);
              const seatCost = seatPricingByPassenger[person.id];
              return (
                <Box key={person.id}>
                  <Typography variant="body1" className="gutter-bottom bold">
                    {passengerName}
                  </Typography>
                  <Box className="line-item">
                    <Typography variant="body1">
                      {textConstants.BASE_FARE}
                    </Typography>
                    <Typography variant="body1">
                      {addCurrency(baseWithoutMargin.fiat.value)}
                    </Typography>
                  </Box>
                  <Box
                    className={`line-item${seatCost == null ? " gutter-bottom" : ""}`}
                  >
                    <Typography variant="body1">
                      {textConstants.TAXES_AND_FEES}
                    </Typography>
                    <Typography variant="body1">
                      {addCurrency(taxesTotal)}
                    </Typography>
                  </Box>
                  {seatCost != null && (
                    <Box className="line-item gutter-bottom">
                      <Typography variant="body1">
                        {textConstants.SEAT_SELECTION}
                      </Typography>
                      <Typography variant="body1">
                        {addCurrency(seatCost)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            }
          )}
          {(cfar || disruptionProtection || chfar) && (
            <>
              <Divider className="line-item gutter-bottom" />
              {disruptionProtection && (
                <Box className="line-item">
                  <Typography variant="body1" className="bold">
                    {textConstants.DISRUPTION_PROTECTION}
                  </Typography>
                  <Typography variant="body1" className="bold">
                    {addCurrency(disruptionProtection.premium.fiat.value)}
                  </Typography>
                </Box>
              )}
              {cfar && (
                <Box className="line-item">
                  <Typography variant="body1" className="bold">
                    {textConstants.CANCEL_FOR_ANY_REASON}
                  </Typography>
                  <Typography variant="body1" className="bold">
                    {addCurrency(cfar.premium.fiat.value)}
                  </Typography>
                </Box>
              )}
              {chfar && (
                <Box className="line-item">
                  <Typography variant="body1" className="bold">
                    {textConstants.CHANGE_FOR_ANY_REASON}
                  </Typography>
                  <Typography variant="body1" className="bold">
                    {addCurrency(chfar.fiat.value)}
                  </Typography>
                </Box>
              )}
            </>
          )}
          {walletItemPayments.map((breakdownPayment) =>
            "breakdown" in breakdownPayment &&
            breakdownPayment.breakdown?.length ? (
              breakdownPayment.breakdown.map((item) => (
                <Box key={item.description} className="line-item positive">
                  <Typography variant="body1" className="bold">
                    {item.description ||
                      textConstants.TRAVEL_CREDITS_LINE_ITEM_APPLIED}
                  </Typography>
                  <Typography variant="body1" className="bold">
                    -{addCurrency(Math.abs(item.amount.amount))}
                  </Typography>
                </Box>
              ))
            ) : (
              <Box className="line-item positive">
                <Typography variant="body1" className="bold">
                  {breakdownPayment.TravelWallet ===
                  PaymentLineItemTravelWalletEnum.TravelWalletCredit
                    ? textConstants.TRAVEL_CREDITS_LINE_ITEM_APPLIED
                    : textConstants.TRAVEL_OFFER_LINE_ITEM_APPLIED}
                </Typography>
                <Typography variant="body1" className="bold">
                  -{addCurrency(Math.abs(breakdownPayment.amount.amount))}
                </Typography>
              </Box>
            )
          )}
          <Box className="line-item gutter-bottom">
            <Typography variant="body1" className="bold">
              {textConstants.TOTAL_COST}
            </Typography>
            <Typography variant="body1" className="bold">
              {addCurrency(effectiveTotal)}
            </Typography>
          </Box>
          <Typography variant="caption" className="line-item gutter-bottom">
            {textConstants.COPY1}
          </Typography>
          {((paymentInfo.numberDisplay &&
            !paymentInfo.numberDisplay?.includes("Unavailable")) ||
            paymentInfo.rewardsDisplay ||
            paymentInfo.creditApplied ||
            paymentInfo.offerApplied) && (
            <Box>
              <Typography variant="body1" className="gutter-bottom bold">
                {textConstants.PAYMENT_INFO}
              </Typography>
              {paymentInfo.numberDisplay && (
                <Typography variant="body1" className="gutter-bottom">
                  {`...${paymentInfo.numberDisplay.slice(-4)}`}
                </Typography>
              )}
              {paymentInfo.rewardsDisplay && (
                <Typography variant="body1" className="gutter-bottom">
                  {`...${paymentInfo.rewardsDisplay.slice(-4)}`}
                </Typography>
              )}
              {paymentInfo.creditApplied &&
                !paymentInfo.rewardsDisplay &&
                !paymentInfo.numberDisplay && (
                  <Typography variant="body1" className="gutter-bottom">
                    {textConstants.TRAVEL_CREDIT_APPLIED}
                  </Typography>
                )}
              {paymentInfo.offerApplied &&
                !paymentInfo.rewardsDisplay &&
                !paymentInfo.numberDisplay && (
                  <Typography variant="body1" className="gutter-bottom">
                    {textConstants.TRAVEL_OFFER_APPLIED}
                  </Typography>
                )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
